import { useCallback, useEffect, useRef, useState } from "react";
import { AppConfig } from "@src/config/AppConfig";
import {
  getMasterDataListCacheEntry,
  invalidateMasterDataListCacheScopes,
  setMasterDataListCacheEntry,
  type MasterDataListCacheScope,
} from "@src/presentation/providers/masterData/masterDataListCache";
import type { MasterDataListFetchResult } from "@src/presentation/providers/masterData/useMasterDataListQuery";

interface UseMasterDataRetailerChildListOptions<TEntity> {
  isOpen: boolean;
  retailerId: number | null;
  brandKey: string;
  loadErrorFallback: string;
  buildCacheKey: (
    brandKey: string,
    retailerId: number,
    page: number,
    pageSize: number,
  ) => string;
  invalidateScopes: MasterDataListCacheScope[];
  fetchPage: (
    retailerId: number,
    page: number,
    pageSize: number,
  ) => Promise<MasterDataListFetchResult<TEntity>>;
  refetchParent: () => void;
}

/**
 * Shared list/cache/fetch cycle for retailer child modals
 * (match rules, raw retailer associations).
 */
export function useMasterDataRetailerChildList<TEntity>({
  isOpen,
  retailerId,
  brandKey,
  loadErrorFallback,
  buildCacheKey,
  invalidateScopes,
  fetchPage,
  refetchParent,
}: UseMasterDataRetailerChildListOptions<TEntity>) {
  const pageSize = AppConfig.getDefaultPaginationLimit();
  const [page, setPage] = useState(1);
  const [activeRetailerId, setActiveRetailerId] = useState(retailerId);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [entities, setEntities] = useState<TEntity[]>([]);

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const invalidateScopesRef = useRef(invalidateScopes);
  invalidateScopesRef.current = invalidateScopes;
  const loadErrorFallbackRef = useRef(loadErrorFallback);
  loadErrorFallbackRef.current = loadErrorFallback;

  if (retailerId !== activeRetailerId) {
    setActiveRetailerId(retailerId);
    setPage(1);
    setEntities([]);
    setTotal(0);
    setError(null);
  }

  const cacheKey =
    isOpen && retailerId != null
      ? buildCacheKey(brandKey, retailerId, page, pageSize)
      : null;
  const cached = cacheKey
    ? getMasterDataListCacheEntry<TEntity>(cacheKey)
    : undefined;
  const items = cached?.items ?? entities;
  const itemTotal = cached?.total ?? total;
  const loading =
    isOpen &&
    retailerId != null &&
    cached == null &&
    entities.length === 0 &&
    error == null;

  const handleSaved = useCallback(() => {
    invalidateMasterDataListCacheScopes(invalidateScopesRef.current);
    setPage(1);
    setEntities([]);
    setTotal(0);
    setError(null);
    setReloadToken((token) => token + 1);
    refetchParent();
  }, [refetchParent]);

  const resetOnClose = useCallback(() => {
    setReloadToken(0);
  }, []);

  useEffect(() => {
    if (!isOpen || retailerId == null || cacheKey == null) {
      return;
    }
    if (getMasterDataListCacheEntry(cacheKey)) {
      return;
    }

    let cancelled = false;

    void fetchPageRef
      .current(retailerId, page, pageSize)
      .then((result) => {
        if (cancelled) return;
        setMasterDataListCacheEntry(cacheKey, {
          items: result.items,
          total: result.total,
          page: result.page,
          limit: result.limit,
        });
        setEntities(result.items);
        setTotal(result.total);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEntities([]);
        setTotal(0);
        setError(
          err instanceof Error ? err.message : loadErrorFallbackRef.current,
        );
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, retailerId, page, pageSize, brandKey, reloadToken, cacheKey]);

  return {
    page,
    setPage,
    pageSize,
    items,
    itemTotal,
    loading,
    error,
    handleSaved,
    resetOnClose,
  };
}
